import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './SideSheet.module.css';
import { useEscapeLayer, useFocusTrap } from './layers';

interface Props {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Pinned under the content (e.g. "Open full guide"). */
  footer?: ReactNode;
}

/**
 * A panel over the page for reading beside the work: slides in from the right on desktop, rises as a
 * bottom sheet on phones (≤ 640 px). Modal (focus trapped, Escape and the scrim close it) and stacked
 * above dialogs (--z-help), so it can be opened from inside a form without closing the form.
 */
export function SideSheet({ open, title, onClose, children, footer }: Props) {
  const { t } = useTranslation();
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEscapeLayer(open, onClose);
  useFocusTrap(panel, open);
  if (!open) return null;
  return createPortal(
    <div
      className={styles.scrim}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={panel} className={styles.panel} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label={t('ui.sideSheet.close')} data-autofocus="">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
